import { useEffect, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { Loader2, Check, X, MapPin, Plus } from "lucide-react";
import RoosterLogo from "@/components/RoosterLogo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * One-click location-removal confirmation. Triggered from a link in the
 * daily forecast email. On mount we DELETE the subscription, then show
 * "Thank you, <first_name>" + an Add-a-new-location CTA.
 */
export default function RemoveLocationPage() {
  const { token, subId } = useParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState("removing"); // removing | done | error
  const [info, setInfo] = useState({ first_name: "", location_label: "" });
  const [message, setMessage] = useState("");
  const firedRef = useRef(false);

  useEffect(() => {
    if (firedRef.current) return; // guard React-StrictMode double-invocation
    firedRef.current = true;
    (async () => {
      try {
        const r = await axios.delete(`${API}/manage/${token}/subscriptions/${subId}`);
        setInfo({
          first_name: r.data?.first_name || "friend",
          location_label: r.data?.location_label || "this location",
        });
        setStatus(r.data?.deleted ? "done" : "error");
        if (!r.data?.deleted) setMessage("Looks like that location was already removed.");
      } catch (e) {
        setMessage(e?.response?.data?.detail || "We couldn't remove that location right now.");
        setStatus("error");
      }
    })();
  }, [token, subId]);

  return (
    <div className="min-h-screen bg-[#F9F9F9]">
      <header className="border-b border-black/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-5 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <RoosterLogo size={36} />
            <div className="font-display text-2xl font-black tracking-tighter">Weathervane</div>
          </Link>
        </div>
      </header>
      <section className="max-w-2xl mx-auto px-6 lg:px-12 py-24">
        <div className="border border-black bg-white p-10" data-testid="remove-location-card">
          {status === "removing" && (
            <>
              <div className="inline-flex items-center justify-center w-12 h-12 border border-black bg-white mb-6">
                <Loader2 className="w-6 h-6 animate-spin" />
              </div>
              <div className="ticker-label mb-3">Removing</div>
              <h1 className="font-display text-4xl font-black tracking-tighter">Updating your subscriptions&hellip;</h1>
              <p className="mt-4 text-neutral-700">One moment while we remove this location from your daily forecast.</p>
            </>
          )}

          {status === "done" && (
            <>
              <div className="inline-flex items-center justify-center w-12 h-12 border border-black bg-[#FFD600] mb-6">
                <Check className="w-6 h-6" />
              </div>
              <div className="ticker-label mb-3">Done</div>
              <h1 className="font-display text-4xl font-black tracking-tighter">
                Thank you, {info.first_name}.
              </h1>
              <p className="mt-6 text-neutral-700 text-lg">
                <span className="inline-flex items-center gap-2">
                  <MapPin className="w-4 h-4 opacity-60" />
                  <strong>{info.location_label}</strong>
                </span>
                <br />
                has been removed from your daily weather report.
              </p>
              <p className="mt-4 text-sm text-neutral-500">
                You won&rsquo;t receive forecasts for this location starting tomorrow.
              </p>

              <div className="mt-10 pt-6 border-t border-black/10">
                <button
                  onClick={() => navigate(`/manage/${token}`)}
                  data-testid="add-new-location-button"
                  className="btn-brutal inline-flex items-center gap-3 px-6 py-4 text-sm tracking-[0.14em] uppercase font-semibold"
                >
                  <Plus className="w-4 h-4" /> Add a new location
                </button>
                <div className="mt-4 text-xs text-neutral-500">
                  Or <Link to={`/manage/${token}`} className="underline" data-testid="manage-link">view & edit all your subscriptions</Link>.
                </div>
              </div>
            </>
          )}

          {status === "error" && (
            <>
              <div className="inline-flex items-center justify-center w-12 h-12 border border-black bg-white mb-6">
                <X className="w-6 h-6" />
              </div>
              <div className="ticker-label mb-3">Hmm</div>
              <h1 className="font-display text-3xl font-black tracking-tighter">
                We couldn&rsquo;t remove that location.
              </h1>
              <p className="mt-4 text-neutral-700">{message}</p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link
                  to={`/manage/${token}`}
                  className="btn-brutal inline-flex items-center gap-3 px-6 py-3 text-sm tracking-[0.14em] uppercase font-semibold"
                  data-testid="manage-fallback-link"
                >
                  Manage subscriptions
                </Link>
                <Link
                  to="/"
                  className="inline-flex items-center gap-2 px-6 py-3 text-sm tracking-[0.14em] uppercase font-semibold border border-black bg-white hover:bg-[#FFD600] transition-colors"
                >
                  Back to home
                </Link>
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
}
